import { App, Spin } from "antd";
import { Form, FormikProvider, useFormik } from "formik";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { useGetApplicantDetailsQuery } from "../../hooks/api/useGetApplicantDetailsQuery";
import { Input } from "../../custom";
import Upload from "../../custom/upload/upload";
import { useGetRequiredDocumentsQuery } from "../../hooks/api/useGetRequiredDocumentsQuery";
import { useGetStudentUploadedDocumentsQuery } from "../../hooks/api/useGetStudentUploadedDocumentsQuery";
import { useUploadRequiredDocumentMutation } from "../../hooks/api/useUploadRequiredDocumentMutation";
import { pageIndexAtom } from "../../utils/store";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import styles from "./styles.module.scss";

interface UploadedRequiredDocumentMeta {
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  viewUrl?: string;
  documentNumber?: string;
}

interface RequiredDocumentsFormValues {
  documents: Record<string, File | null>;
  documentNumbers: Record<string, string>;
}

const getUploadedDocumentViewUrl = (response: any, fallbackUrl?: string) => {
  const responseData = response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return (
    responseData?.fileUrl ||
    responseData?.documentUrl ||
    responseData?.url ||
    response?.fileUrl ||
    response?.documentUrl ||
    response?.url ||
    fallbackUrl
  );
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(file);
  });

const normalizeAllowedFileTypes = (allowedFileTypes?: string) =>
  allowedFileTypes
    ?.split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (item.startsWith(".") ? item.toLowerCase() : `.${item.toLowerCase()}`)) ?? [];

const getDocumentKey = (documentId: number) => `${documentId}`;

const RequiredDocument = () => {
  const { notification } = App.useApp();
  const { applicantDetails } = useGetApplicantDetailsQuery();
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const { documents, isLoading, isError, error } = useGetRequiredDocumentsQuery({
    programTypeId: applicantDetails?.programTypeId,
  });
  const { documents: studentUploadedDocuments, refetch: refetchUploadedDocuments } =
    useGetStudentUploadedDocumentsQuery({
      applicantId: applicantDetails?.applicantId,
    });
  const { uploadHandler, isLoading: isUploading } =
    useUploadRequiredDocumentMutation();

  // Source of truth for "already uploaded" is the backend, scoped to this
  // applicant. Do not use localStorage here: it isn't scoped per applicant,
  // so a different applicant on the same browser would see a previous
  // applicant's uploads as if they were their own.
  const uploadedDocuments = useMemo<Record<string, UploadedRequiredDocumentMeta>>(
    () =>
      (studentUploadedDocuments ?? []).reduce<
        Record<string, UploadedRequiredDocumentMeta>
      >((accumulator, item) => {
        accumulator[getDocumentKey(item.requiredDocumentId)] = {
          fileName: item.fileName,
          fileSize: (item.fileSizeInMB ?? 0) * 1024 * 1024,
          uploadedAt: item.uploadedDate,
          viewUrl: item.fileUrl,
          documentNumber: item.documentNumber,
        };
        return accumulator;
      }, {}),
    [studentUploadedDocuments],
  );
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, UploadedRequiredDocumentMeta>
  >({});
  const [uploadingDocumentId, setUploadingDocumentId] = useState<number | null>(
    null,
  );

  const initialValues = useMemo<RequiredDocumentsFormValues>(
    () => ({
      documents:
        documents?.reduce<Record<string, File | null>>((accumulator, item) => {
          accumulator[getDocumentKey(item.id)] = null;
          return accumulator;
        }, {}) ?? {},
      documentNumbers:
        documents?.reduce<Record<string, string>>((accumulator, item) => {
          const documentKey = getDocumentKey(item.id);
          accumulator[documentKey] =
            uploadedDocuments[documentKey]?.documentNumber ?? "";
          return accumulator;
        }, {}) ?? {},
    }),
    [documents, uploadedDocuments],
  );

  const formik = useFormik<RequiredDocumentsFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async () => {
      setPageIndex(pageIndex + 1);
    },
  });

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleFileChange =
    (item: RequiredDocument) => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const documentKey = getDocumentKey(item.id);

      if (!file) {
        return;
      }

      const documentNumber = formik.values.documentNumbers?.[documentKey]?.trim();

      if (item.isTextFieldRequired && !documentNumber) {
        notification.error({
          message: "Certificate or Document number required",
          description: `Please enter the certificate number for ${item.name} before uploading.`,
        });
        event.target.value = "";
        return;
      }

      const allowedFormats = normalizeAllowedFileTypes(item.allowedFileTypes);
      const fileExtension = file.name.includes(".")
        ? `.${file.name.split(".").pop()?.toLowerCase()}`
        : "";
      const maxFileSizeInBytes = item.maxFileSizeInMB * 1024 * 1024;

      if (allowedFormats.length > 0 && !allowedFormats.includes(fileExtension)) {
        notification.error({
          message: "Invalid file type",
          description: `Allowed file types: ${allowedFormats.join(", ")}`,
        });
        event.target.value = "";
        return;
      }

      if (maxFileSizeInBytes > 0 && file.size > maxFileSizeInBytes) {
        notification.error({
          message: "File too large",
          description: `${item.name} must be less than ${item.maxFileSizeInMB}MB`,
        });
        event.target.value = "";
        return;
      }

      const uploadedFileMeta = {
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
      };

      await formik.setFieldValue(`documents.${documentKey}`, file);
      setSelectedFiles((currentValue) => ({
        ...currentValue,
        [documentKey]: uploadedFileMeta,
      }));

      try {
        setUploadingDocumentId(item.id);
        const localPreviewUrl = await readFileAsDataUrl(file);
        const response = await uploadHandler({
          requiredDocumentId: item.id,
          file,
          documentNumber,
          hasTextField: item.hasTextField,
          isTextFieldRequired: item.isTextFieldRequired,
        });

        const resolvedViewUrl = getUploadedDocumentViewUrl(
          response,
          localPreviewUrl,
        );

        setSelectedFiles((currentValue) => ({
          ...currentValue,
          [documentKey]: { ...uploadedFileMeta, viewUrl: resolvedViewUrl },
        }));

        await refetchUploadedDocuments();
        await formik.setFieldValue(`documents.${documentKey}`, null);
      } catch (_error) {
        setSelectedFiles((currentValue) => {
          const updatedValue = { ...currentValue };
          delete updatedValue[documentKey];
          return updatedValue;
        });
        await formik.setFieldValue(`documents.${documentKey}`, null);
        event.target.value = "";
      } finally {
        setUploadingDocumentId(null);
      }
    };

  const is404 = isError && (error as any)?.response?.status === 404;

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError && !is404) {
    return (
      <FormikProvider value={formik}>
        <Form>
          <section className={styles.section}>
            <h2>Required Documents</h2>
          </section>
          <hr />
          <div className={styles.section}>
            <p>Unable to load required documents. You may proceed to the next step.</p>
            <br />
            <OnboardingButtonGroup
              onClick={handlePrevClick}
              onNextClick={formik.submitForm}
            />
          </div>
        </Form>
      </FormikProvider>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <section className={styles.section}>
          <h2>Required Documents</h2>
          <br />
          <p>Upload the documents requested for your application.</p>
        </section>
        <hr />

        <div className={styles.section}>
          {documents?.length ? (
            <div className={styles.formWrapper}>
              {documents.map((item) => {
                const allowedFormats = normalizeAllowedFileTypes(
                  item.allowedFileTypes,
                );
                const documentKey = getDocumentKey(item.id);
                const currentFile =
                  selectedFiles[documentKey] || uploadedDocuments[documentKey];

                return (
                  <div key={item.id}>
                    {item.hasTextField && (
                      <Input
                        type="text"
                        name={`documentNumbers.${documentKey}`}
                        label={`${item.name} Certificate Number`}
                        placeholder="Enter certificate number"
                        asterisk={item.isTextFieldRequired}
                      />
                    )}
                    <Upload
                      name={`documents.${documentKey}`}
                      label={`${item.name}${item.isRequired ? " *" : ""}`}
                      description={
                        <div>
                          <p>{item.description}</p>
                          <small>
                            Max size: {item.maxFileSizeInMB}MB
                            {allowedFormats.length > 0
                              ? ` | Allowed: ${allowedFormats.join(", ")}`
                              : ""}
                          </small>
                        </div>
                      }
                      fileName={currentFile?.fileName}
                      fileSize={currentFile?.fileSize}
                      allowedFormats={allowedFormats}
                      accept={allowedFormats.join(",")}
                      onChange={handleFileChange(item)}
                    />
                    {uploadingDocumentId === item.id && (
                      <div className={styles.loader}>
                        <Spin size="small" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No required documents found for this application.</p>
          )}

          <OnboardingButtonGroup
            onClick={handlePrevClick}
            onNextClick={formik.submitForm}
            isLoading={isUploading}
            disabled={uploadingDocumentId !== null}
          />
        </div>
      </Form>
    </FormikProvider>
  );
};

export default RequiredDocument;
